import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AuthCallback from "./components/AuthCallback";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import WrapWheelPage from "./pages/WrapWheelPage";
import SwagShopPage from "./pages/SwagShopPage";
import ComplianceDirectoryPage from "./pages/ComplianceDirectoryPage";
import OvernightReviewPage from "./pages/OvernightReviewPage";
import DesignatedCaregiverPage from "./pages/DesignatedCaregiverPage";
import ProfitabilityPage from "./pages/ProfitabilityPage";
import ClientsPage from "./pages/ClientsPage";
import HandbookPage from "./pages/HandbookPage";
import SetupUploadsPage from "./pages/SetupUploadsPage";
import NewClientPage from "./pages/NewClientPage";
import GuidedIntakePage from "./pages/GuidedIntakePage";
import IntakeBankPage from "./pages/IntakeBankPage";
import ClientIntakePage from "./pages/ClientIntakePage";
import PublicIntakePage from "./pages/PublicIntakePage";
import DocumentsPage from "./pages/DocumentsPage";
import ObservationsPage from "./pages/ObservationsPage";
import ComplaintsPage from "./pages/ComplaintsPage";
import IncidentLogPage from "./pages/IncidentLogPage";
import PaperTestsPage from "./pages/PaperTestsPage";
import MissedCallsPage from "./pages/MissedCallsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import CarePlanRecordsPage from "./pages/CarePlanRecordsPage";
import CarePlanReviewPage from "./pages/CarePlanReviewPage";
import CarePlanComparisonPage from "./pages/CarePlanComparisonPage";
import ServiceAgreementEditorPage from "./pages/ServiceAgreementEditorPage";
import DocumentLibraryPage from "./pages/DocumentLibraryPage.jsx";
import PaperworkPage from "./pages/PaperworkPage";
import PaperworkUpdatePage from "./pages/PaperworkUpdatePage";
import TemplatesPage from "./pages/TemplatesPage";
import QuestionBankPage from "./pages/QuestionBankPage";
import FillSignPage from "./pages/FillSignPage";
import SharePage from "./pages/SharePage";
import IntakePage from "./pages/IntakePage";
import IntakeStartPage from "./pages/IntakeStartPage";
import PublicTrainingPage from "./pages/PublicTrainingPage";
import PublicScreeningPage from "./pages/PublicScreeningPage";
import FreeCarePage from "./pages/FreeCarePage";
import LandingPage from "./pages/LandingPage";
import GoogleAdLandingPage from "./pages/GoogleAdLandingPage";
import GoogleAnalyticsPageViews from "./GoogleAnalyticsPageViews";
import UetBootstrap from "./UetBootstrap";
import PrivatePayPage from "./pages/PrivatePayPage";
import PaidCaregiverPage from "./pages/PaidCaregiverPage";
import PayRatesPage from "./pages/PayRatesPage";
import ImageGalleryPage from "./pages/ImageGalleryPage";
import ReviewsPage from "./pages/ReviewsPage";
import ReviewsAdminPage from "./pages/ReviewsAdminPage";
import CompetitionPage from "./pages/CompetitionPage";
import ClientPortalAccessPage from "./pages/ClientPortalAccessPage";
import AgreementFormPage from "./pages/AgreementFormPage";
import ApplicationViewPage from "./pages/ApplicationViewPage";
import ClientHandbookPublicPage from "./pages/ClientHandbookPublicPage";
import FieldPage from "./pages/FieldPage";
import ApplyPage from "./pages/ApplyPage";
import PublicReferencePage from "./pages/PublicReferencePage";
import InterviewFormPage from "./pages/InterviewFormPage";
import TrainingPage from "./pages/TrainingPage";
import TaskCodesPage from "./pages/TaskCodesPage";
import MeetingsPage from "./pages/MeetingsPage";
import MeetingRoomPage from "./pages/MeetingRoomPage";
import CallLogPage from "./pages/CallLogPage";
import BillingPage from "./pages/BillingPage";
import PaymentThanksPage from "./pages/PaymentThanksPage";
import SettingsPage from "./pages/SettingsPage";
import GuidelinesPage from "./pages/GuidelinesPage";
import ReferencesPage from "./pages/ReferencesPage";
import ReferralsPage from "./pages/ReferralsPage";
import StaffCarePlanPage from "./pages/StaffCarePlanPage";
import HiringRegisterPage from "./pages/HiringRegisterPage";
import PoliciesPage from "./pages/PoliciesPage";
import KnowledgeBasePage from "./pages/KnowledgeBasePage";
import TrainingBasePage from "./pages/TrainingBasePage";
import SurveyAnswersPage from "./pages/SurveyAnswersPage";
import AuditWorkbookPage from "./pages/AuditWorkbookPage";
import AssistantPage from "./pages/AssistantPage";
import OrientationPage from "./pages/OrientationPage";
import TrainingRadarPage from "./pages/TrainingRadarPage";
import NotificationsPage from "./pages/NotificationsPage";
import OnboardingPage from "./pages/OnboardingPage";
import MyProfilePage from "./pages/MyProfilePage";
import LikenessPage from "./pages/LikenessPage";
import WebsitePhotosAdminPage from "./pages/WebsitePhotosAdminPage";
import ClientPortalPage from "./pages/ClientPortalPage";
import OfferSignPage from "./pages/OfferSignPage";
import CalendarPage from "./pages/CalendarPage";
import PrintCenterPage from "./pages/PrintCenterPage";
import EmployeeFilesPage from "./pages/EmployeeFilesPage";
import EmployeeFilePage from "./pages/EmployeeFilePage";
import EmployeeVisitsPage from "./pages/EmployeeVisitsPage";
import NotesPage from "./pages/NotesPage";
import UsersPage from "./pages/UsersPage";
import ShiftLogPage from "./pages/ShiftLogPage";
import IncidentsPage from "./pages/IncidentsPage";
import ReportsPage from "./pages/ReportsPage";
import ReconciliationPage from "./pages/ReconciliationPage";
import QualityPage from "./pages/QualityPage";
import EvvPage from "./pages/EvvPage";
import MissedPunchFormPage from "./pages/MissedPunchFormPage";
import FormsPage from "./pages/FormsPage";
import HiringPage from "./pages/HiringPage";
import PrescreenPage from "./pages/PrescreenPage";
import InterviewConfirmPage from "./pages/InterviewConfirmPage";
import DrugTestConsentPage from "./pages/DrugTestConsentPage";
import PublicSitePage from "./pages/PublicSitePage";
import GuidePage from "./pages/GuidePage";
import AuditPage from "./pages/AuditPage";
import PayerAuditsPage from "./pages/PayerAuditsPage";
import PolicyDetailPage from "./pages/PolicyDetailPage";
import TrainingCreatorPage from "./pages/TrainingCreatorPage";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return <Layout>{children}</Layout>;
};

const ProtectedBare = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const HomeGate = () => {
  return <PublicSitePage />;
};

function LegacyIntakeRedirect() {
  const { id } = useParams();
  return <Navigate to={id ? `/clients/intake/${id}` : "/clients/intake"} replace />;
}

// Public marketing domain (silverhomecare.com): anonymous visitors get ONLY the public website.
// The sign-in page is allowed so caregivers can enter via the footer link; once signed in,
// their portal works there too. (Owner 2026-09-30, updated 2026-10-02)
const PUBLIC_SITE_HOSTS = ["silverhomecare.com", "www.silverhomecare.com"];
const ON_PUBLIC_SITE = PUBLIC_SITE_HOSTS.includes(window.location.hostname);
const PUBLIC_PATH = /^\/(site\/?|apply|login\/?|start\/?|client-handbook\/?|intake-start\/?|intake\/|intake-q\/|train\/|portal\/|share\/|screen\/|offer\/|interview\/|reference-check\/|pay\/thanks\/?|documents\/drug-testing-consent\/?|forms\/agreement\/?)?$/;

function PublicSiteGate({ children }) {
  const loc = useLocation();
  const { user, loading } = useAuth();
  if (ON_PUBLIC_SITE && !loading && !user && !PUBLIC_PATH.test(loc.pathname)) {
    return <Navigate to="/" replace />;
  }
  return children;
}


// Emergent-managed Google Auth: intercept the OAuth return (session_id in the URL fragment)
// synchronously during render, before any route/gate runs. (Playbook: read useLocation().hash.)
function SessionCatcher({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <GoogleAnalyticsPageViews />
        <UetBootstrap />
        <SessionCatcher>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <PublicSiteGate>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/payer-audits" element={<Protected><PayerAuditsPage /></Protected>} />
            <Route path="/policies/:pid" element={<Protected><PolicyDetailPage /></Protected>} />
            <Route path="/share/:token" element={<SharePage />} />
            <Route path="/portal/:token" element={<ClientPortalPage />} />
            <Route path="/intake-q/:token" element={<PublicIntakePage />} />
            <Route path="/pay/thanks" element={<PaymentThanksPage />} />
            <Route path="/offer/:token" element={<OfferSignPage />} />
            <Route path="/interview/:token" element={<InterviewConfirmPage />} />
            <Route path="/documents/drug-testing-consent" element={<DrugTestConsentPage />} />
            <Route path="/intake/:token" element={<IntakePage />} />
            <Route path="/intake-start" element={<IntakeStartPage />} />
            <Route path="/train/:token" element={<PublicTrainingPage />} />
            <Route path="/screen/:token" element={<PublicScreeningPage />} />
            <Route path="/apply" element={<ApplyPage />} />
            <Route path="/free-care" element={<Navigate to="/" replace />} />
            <Route path="/start" element={<LandingPage />} />
            <Route path="/google-ad" element={<GoogleAdLandingPage />} />
            <Route path="/private-pay" element={<PrivatePayPage />} />
            <Route path="/chc" element={<Navigate to="/" replace />} />
            <Route path="/get-paid-to-care-for-a-loved-one-in-pennsylvania" element={<PaidCaregiverPage />} />
            <Route path="/get-paid-to-care-for-a-loved-one-in-pennsylvania/bucks-county" element={<PaidCaregiverPage county="Bucks County" slug="bucks-county" />} />
            <Route path="/get-paid-to-care-for-a-loved-one-in-pennsylvania/philadelphia" element={<PaidCaregiverPage county="Philadelphia" slug="philadelphia" />} />
            <Route path="/how-much-do-family-caregivers-get-paid-in-pennsylvania" element={<PayRatesPage />} />
            <Route path="/image-gallery" element={<ImageGalleryPage />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/admin/reviews" element={<Protected adminOnly><ReviewsAdminPage /></Protected>} />
            <Route path="/competition" element={<Protected><CompetitionPage /></Protected>} />
            <Route path="/client-portal" element={<ClientPortalAccessPage />} />
            <Route path="/forms/agreement" element={<AgreementFormPage />} />
            <Route path="/hiring/application/:aid" element={<Protected><ApplicationViewPage /></Protected>} />
            <Route path="/field" element={<ProtectedBare><FieldPage /></ProtectedBare>} />
            <Route path="/reference-check/:token" element={<PublicReferencePage />} />
            <Route path="/hiring/prescreen/:aid" element={<Protected><PrescreenPage /></Protected>} />
            <Route path="/hiring/interview/:aid" element={<Protected><InterviewFormPage /></Protected>} />
            <Route path="/" element={<HomeGate />} />
            <Route path="/site" element={<PublicSitePage />} />
            <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
            <Route path="/wrap-wheel" element={<Protected><WrapWheelPage /></Protected>} />
            <Route path="/swag-shop" element={<Protected><SwagShopPage /></Protected>} />
            <Route path="/compliance-directory" element={<Protected adminOnly><ComplianceDirectoryPage /></Protected>} />
            <Route path="/overnight-review" element={<Protected adminOnly><OvernightReviewPage /></Protected>} />
            <Route path="/designated-caregiver" element={<Protected><DesignatedCaregiverPage /></Protected>} />
            <Route path="/profitability" element={<Protected adminOnly><ProfitabilityPage /></Protected>} />
            <Route path="/clients" element={<Protected><ClientsPage /></Protected>} />
            <Route path="/clients/:id/careplan-review" element={<Protected adminOnly><CarePlanReviewPage /></Protected>} />
            <Route path="/careplan-records" element={<Protected adminOnly><CarePlanRecordsPage /></Protected>} />
            <Route path="/careplan-records/:recordId/compare" element={<Protected adminOnly><CarePlanComparisonPage /></Protected>} />
            <Route path="/handbook" element={<Protected><HandbookPage variant="employee" /></Protected>} />
            <Route path="/client-handbook" element={<ClientHandbookPublicPage />} />
            <Route path="/quick-reference" element={<Protected><HandbookPage variant="reference" /></Protected>} />
            <Route path="/clients/new" element={<Protected><NewClientPage /></Protected>} />
            <Route path="/intake-form/new" element={<Protected><LegacyIntakeRedirect /></Protected>} />
            <Route path="/intake-form/:id" element={<Protected><LegacyIntakeRedirect /></Protected>} />
            <Route path="/clients/intake" element={<Protected><ClientIntakePage /></Protected>} />
            <Route path="/clients/intake/:cid" element={<Protected><ClientIntakePage /></Protected>} />
            <Route path="/paperwork-update" element={<Protected><PaperworkUpdatePage /></Protected>} />
            <Route path="/admin/intake-bank" element={<Protected><IntakeBankPage /></Protected>} />
            <Route path="/observations" element={<Protected adminOnly><ObservationsPage /></Protected>} />
            <Route path="/complaints" element={<Protected adminOnly><ComplaintsPage /></Protected>} />
            <Route path="/incidents" element={<Protected adminOnly><IncidentLogPage /></Protected>} />
            <Route path="/missed-calls" element={<Protected adminOnly><MissedCallsPage /></Protected>} />
            <Route path="/clients/:id" element={<Protected><ClientDetailPage /></Protected>} />
            <Route path="/clients/:id/service-agreement" element={<Protected><ServiceAgreementEditorPage /></Protected>} />
            <Route path="/clients/:id/documents" element={<Protected><DocumentsPage /></Protected>} />
            <Route path="/clients/:id/portal" element={<Protected><ClientPortalPage /></Protected>} />
            <Route path="/clients/:id/paperwork" element={<Protected><PaperworkPage /></Protected>} />
            <Route path="/clients/:id/paperwork/:docKey" element={<Protected><FillSignPage /></Protected>} />
            <Route path="/clients/:id/log" element={<Protected><ShiftLogPage /></Protected>} />
            <Route path="/clients/:id/incidents" element={<Protected><IncidentsPage /></Protected>} />
            <Route path="/users" element={<Protected><UsersPage /></Protected>} />
            <Route path="/reports" element={<Protected><ReportsPage /></Protected>} />
            <Route path="/reconciliation" element={<Protected><ReconciliationPage /></Protected>} />
            <Route path="/quality" element={<Protected><QualityPage /></Protected>} />
            <Route path="/evv" element={<Protected><EvvPage /></Protected>} />
            <Route path="/missed-punch/new" element={<Protected><MissedPunchFormPage /></Protected>} />
            <Route path="/forms" element={<Protected><FormsPage /></Protected>} />
            <Route path="/documents" element={<Protected><DocumentLibraryPage /></Protected>} />
            <Route path="/hiring" element={<Protected><HiringPage /></Protected>} />
            <Route path="/calendar" element={<Protected adminOnly><CalendarPage /></Protected>} />
            <Route path="/print-center" element={<Protected adminOnly><PrintCenterPage /></Protected>} />
            <Route path="/admin/website-photos" element={<Protected adminOnly><WebsitePhotosAdminPage /></Protected>} />
            <Route path="/employee-files" element={<Protected><EmployeeFilesPage /></Protected>} />
            <Route path="/employee-files/:bid" element={<Protected><EmployeeFilesPage /></Protected>} />
            <Route path="/documents/:docKey" element={<Protected><FillSignPage /></Protected>} />
            <Route path="/guidelines" element={<Protected><GuidelinesPage /></Protected>} />
            <Route path="/references" element={<Protected><ReferencesPage /></Protected>} />
            <Route path="/my-careplan/:clientId" element={<Protected><StaffCarePlanPage /></Protected>} />
            <Route path="/referrals" element={<Protected><ReferralsPage /></Protected>} />
            <Route path="/hiring-register" element={<Protected><HiringRegisterPage /></Protected>} />
            <Route path="/policies" element={<Protected><PoliciesPage /></Protected>} />
            <Route path="/knowledge" element={<Protected><KnowledgeBasePage /></Protected>} />
            <Route path="/training-base" element={<Protected><TrainingBasePage /></Protected>} />
            <Route path="/survey-answers" element={<Protected><SurveyAnswersPage /></Protected>} />
            <Route path="/audit-questionnaire" element={<Protected><AuditWorkbookPage /></Protected>} />
            <Route path="/assistant" element={<Protected><AssistantPage /></Protected>} />
            <Route path="/orientation" element={<Protected><OrientationPage /></Protected>} />
            <Route path="/setup-uploads" element={<Protected adminOnly><SetupUploadsPage /></Protected>} />
            <Route path="/uploads" element={<Protected adminOnly><SetupUploadsPage /></Protected>} />
            <Route path="/onboarding" element={<Protected><OnboardingPage /></Protected>} />
            <Route path="/training-radar" element={<Navigate to="/training" replace />} />
            <Route path="/notifications" element={<Protected><NotificationsPage /></Protected>} />
            <Route path="/training" element={<Protected><TrainingPage /></Protected>} />
            <Route path="/training-tests" element={<Protected adminOnly><PaperTestsPage /></Protected>} />
            <Route path="/task-codes" element={<Protected><TaskCodesPage /></Protected>} />
            <Route path="/meetings" element={<Protected><MeetingsPage /></Protected>} />
            <Route path="/meetings/:mid" element={<Protected><MeetingRoomPage /></Protected>} />
            <Route path="/call-log" element={<Protected><CallLogPage /></Protected>} />
            <Route path="/billing" element={<Protected><BillingPage /></Protected>} />
            <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
            <Route path="/profile" element={<Protected><MyProfilePage /></Protected>} />
            <Route path="/likeness" element={<Protected><LikenessPage /></Protected>} />
            <Route path="/likeness/:token" element={<LikenessPage />} />
            <Route path="/team/:uid" element={<Protected><EmployeeFilePage /></Protected>} />
            <Route path="/team/:uid/visits" element={<Protected><EmployeeVisitsPage /></Protected>} />
            <Route path="/notes" element={<Protected><NotesPage /></Protected>} />
            <Route path="/templates" element={<Protected><TemplatesPage /></Protected>} />
            <Route path="/questions" element={<Protected><QuestionBankPage /></Protected>} />
            <Route path="/guide" element={<Protected><GuidePage /></Protected>} />
            <Route path="/audit" element={<Protected><AuditPage /></Protected>} />
            <Route path="/training-creator" element={<Protected><TrainingCreatorPage /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </PublicSiteGate>
        </AuthProvider>
        </SessionCatcher>
      </BrowserRouter>
    </div>
  );
}

export default App;
